import { useCallback, useEffect, useState } from "react";
import { TriggeredHook } from "sonobello.utilities.react";

import { ContactResult } from "../../../types/ContactResult";
import { ContactResultRequest, toContactResultRequest } from "../../../types/ContactResultRequest";
import { ContactResultResponse, toDomainContactResult } from "../../../types/ContactResultResponse";
import { DisqualifyingReason, DisqualifyingReasonsType } from "../../../types/DisqualifyingReason";
import { useObx } from "../../../hooks/useObx";
import ApiRequestPaths from "../../../constants/ApiRequestPaths";
import MedicalNonCanContactResult from "../types/MedicalNonCanContactResult";

/** A hook which dispatches a network request to create a disqualifying contact result for medical non-candidacy.
 * @remarks This hook returns a usable value even if the network request fails.
 */
const useResilientCreateMedicalNonCanContactResult: TriggeredHook<
  ContactResult,
  undefined,
  boolean,
  { contactId: string; patientId: string; patientLeadId: string; disqualifyingReasons: DisqualifyingReason[] }
> = ({ contactId, patientId, patientLeadId, disqualifyingReasons }) => {
  const [returnValue, setReturnValue] = useState<ContactResult>();

  const { res, err, loading, setReq } = useObx<ContactResultResponse, ContactResultRequest>("Post ContactResult", {
    method: "post",
    url: ApiRequestPaths.postContactResult(contactId)
  });

  const execute = useCallback(() => {
    const reason = DisqualifyingReason.GetReason(disqualifyingReasons, DisqualifyingReasonsType.ObxMedicalNoncandidate);
    setReq(r => ({
      ...r,
      payload: toContactResultRequest(new MedicalNonCanContactResult(patientId, patientLeadId, reason.id))
    }));
  }, [setReq]);

  useEffect(() => {
    if (!res && !err) return;
    const reason = DisqualifyingReason.GetReason(disqualifyingReasons, DisqualifyingReasonsType.ObxMedicalNoncandidate);
    setReturnValue(
      res
        ? toDomainContactResult(res.payload) // opportunityId (API) → leadId (domain)
        : new MedicalNonCanContactResult(patientId, patientLeadId, reason.id)
    );
  }, [res, err]);

  return { execute, isLoading: loading, result: returnValue || null, error: Boolean(err) };
};

export default useResilientCreateMedicalNonCanContactResult;
